import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { getColorForString } from "@/features/utils/colors";
import { cn } from "@/lib/utils";

/**
 * The one size scale for every avatar — a person, a company, an agent. Each of
 * the three molecules had its own, so "md" was 32px for a user and 80px for an
 * agent. The text size is for the initials a fallback shows.
 */
export const avatarSizes = {
  xs: "size-4 text-[8px]",
  sm: "size-6 text-xs",
  md: "size-8 text-sm",
  lg: "size-12 text-base",
  xl: "size-20 text-xl",
  "2xl": "size-32 text-3xl",
  "3xl": "size-48 text-5xl",
} as const;

export type AvatarSize = keyof typeof avatarSizes;

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
    size?: AvatarSize;
  }
>(({ className, size, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex size-10 shrink-0 overflow-hidden rounded-full",
      size && avatarSizes[size],
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, children, style, ...props }, ref) => {
  // Generate background color based on children text
  const backgroundColor = React.useMemo(
    () => getColorForString(typeof children === "string" ? children : ""),
    [children],
  );

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full text-white font-medium",
        className,
      )}
      style={{
        backgroundColor,
        ...style,
      }}
      {...props}
    >
      {children}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
